/**
 * The chef's selected date range, shared across the two chef surfaces
 * (`/orders/kitchen` and `/shopping-list`).
 *
 * Why a store rather than the URL alone: chefs move between these surfaces by
 * clicking the sidebar, which renders bare `<Link to={item.href}>` with no
 * search string (`components/ordo-sidebar.tsx`) — and both pages can also mount
 * at `/` with no params at all (`routes/index.tsx` renders them in place for
 * chefs whose first nav item is that slug). A URL-only scheme would need the
 * shared sidebar — which parents and schools use too — to append a
 * chef-specific range to particular hrefs, and every entry point that missed it
 * would silently reset the chef again. The URL is still synced ON TOP of this
 * store by `hooks/chefs/useChefDateRange`, so links stay shareable.
 *
 * Written ONLY on a real user change. An untouched store leaves each surface on
 * its own cold-start default (kitchen daily, shopping list weekly); once the
 * chef expresses a choice, that choice travels with them.
 *
 * `anchorKey` is a local `YYYY-MM-DD` string rather than a `Date`: it survives
 * JSON persistence without a reviver, and it is `===`-comparable, so the `range`
 * memo downstream is stable by construction (an unstable `Date` identity would
 * recompute `buildServiceDays` -> `campusesByDay` on every render).
 *
 * Persisted to sessionStorage: the range holds across a reload within the
 * working session and resets in a fresh tab.
 */

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type { StoredDateRange } from '@/lib/chefs/date-range-url'
import { type DateRange, isoDate } from '@/lib/chefs/service-days'

interface ChefDateRangeState {
  /** Local `YYYY-MM-DD`. `null` until the chef actually changes the range. */
  anchorKey: string | null
  granularity: DateRange['granularity'] | null
  /** Inclusive end of a `custom` window; `null` for the derived granularities. */
  endKey: string | null
  /**
   * The day the range was chosen on. `useChefDateRange` discards a stored range
   * whose stamp isn't today, so a tab left open overnight doesn't strand the
   * chef on yesterday. See `resolveDateRange`.
   */
  seededOn: string | null
  setRange: (range: DateRange, today?: Date) => void
  clear: () => void
}

export const useChefDateRangeStore = create<ChefDateRangeState>()(
  persist(
    (set) => ({
      anchorKey: null,
      granularity: null,
      endKey: null,
      seededOn: null,
      setRange: (range, today = new Date()) =>
        set({
          // `isoDate` is local-date based — matches how the pages build their
          // query start/end keys. A UTC serialization would shift the anchor a
          // day for chefs west of Greenwich.
          anchorKey: isoDate(range.anchor),
          granularity: range.granularity,
          endKey: range.granularity === 'custom' ? isoDate(range.end) : null,
          seededOn: isoDate(today),
        }),
      clear: () => set({ anchorKey: null, granularity: null, endKey: null, seededOn: null }),
    }),
    {
      name: 'ordo.chef-date-range',
      storage: createJSONStorage(() => sessionStorage),
      // Still v1 after `endKey` was added: zustand's default merge lays the
      // persisted blob over the initial state, so a pre-custom payload keeps
      // `endKey: null` — and `storedRange` already rejects a custom entry
      // without one. A bump with no `migrate` would only add a console warning.
      version: 1,
    },
  ),
)

/**
 * The store's range in the shape `resolveDateRange` consumes, or `null` while
 * it's unseeded (the chef hasn't changed the range in this session yet).
 *
 * NOT a zustand selector — it allocates, and zustand 5 loops on a subscription
 * whose snapshot identity changes every read. Call it on a snapshot
 * (`storedRange(useChefDateRangeStore.getState())`, or inside a `useMemo` over
 * individually-subscribed primitives, which is what `useChefDateRange` does).
 */
export function storedRange(
  state: Pick<ChefDateRangeState, 'anchorKey' | 'granularity' | 'endKey' | 'seededOn'>,
): StoredDateRange | null {
  if (!state.anchorKey || !state.granularity || !state.seededOn) {
    return null
  }
  if (state.granularity !== 'custom') {
    return { anchorKey: state.anchorKey, granularity: state.granularity, seededOn: state.seededOn }
  }
  // `endKey` is NOT part of the guard above — it's legitimately absent for the
  // derived granularities. A custom entry without one names no window, which is
  // exactly how a session persisted before the custom view rehydrates.
  if (!state.endKey) {
    return null
  }
  return {
    granularity: 'custom',
    anchorKey: state.anchorKey,
    endKey: state.endKey,
    seededOn: state.seededOn,
  }
}
